import PropTypes from "prop-types";
import { Formik, Form, Field } from "formik";
import { Row, Col } from "react-grid-system";
import { StyledField } from "../../styles/StyledField";
import CancelIcon from "../../assets/img/icons/cancel.svg";
import SaveIcon from "../../assets/img/icons/save.svg";
import * as Yup from "yup";
import { StyledProfileDetailsDataButtons } from "../../styles/StyledProfileDetails/StyledProfileDetailsData";
import { Button } from "../Button";
import { Error } from "../Error";
import { patchUserProfile } from "../../api/user/profile";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";

const ValidationSchema = Yup.object().shape({
	firstName: Yup.string().min(2, "Too short").max(50, "Too long").required("Required"),
	lastName: Yup.string().min(2, "Too short").max(50, "Too long").required("Required"),
	phone: Yup.string()
		.min(9, "Too short")
		.max(12, "Too long")
		.matches(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s./0-9]*$/, "Phone number is not valid")
		.required("Required"),
	email: Yup.string().email().min(2, "Too short").max(30, "Too long").required("Required"),
	roleInOrganization: Yup.string().min(2, "Too short").max(50, "Too long"),
	website: Yup.string().min(2, "Too short").max(50, "Too long"),
	officeAddress: Yup.string().min(2, "Too short").max(50, "Too long"),
	privateAddress: Yup.string().min(2, "Too short").max(50, "Too long"),
});

export default function ProfileDetailsDataEditForm({ data, setIsEditMode }) {
	const axiosHandler = useAxiosPrivate();
	const { firstName, lastName, roleInOrganization, phone, email, website, officeAddress, privateAddress, about } = data;
	const initialValues = { firstName, lastName, roleInOrganization, phone, email, website, officeAddress, privateAddress, about };

	const handleDataUpdate = async (values) => {
		await patchUserProfile(axiosHandler.axiosPrivate, () => setIsEditMode(false), values);
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={ValidationSchema}
			onSubmit={(values) => {
				handleDataUpdate(values);
			}}
		>
			{({ errors, touched, isValid }) => (
				<Form>
					<Row>
						<Col sm={12} lg={6} xl={3}>
							<StyledField>
								<label>First name</label>
								<Field id="firstName" name="firstName" data-testid="firstName" />
								{errors.firstName && touched.firstName && <Error>{errors.firstName}</Error>}
							</StyledField>
							<StyledField>
								<label>Last name</label>
								<Field id="lastName" name="lastName" data-testid="lastName" />
								{errors.lastName && touched.lastName && <Error>{errors.lastName}</Error>}
							</StyledField>
							<StyledField mb="20px">
								<label>Role</label>
								<Field id="roleInOrganization" name="roleInOrganization" data-testid="roleInOrganization" />
								{errors.roleInOrganization && touched.roleInOrganization && <Error>{errors.roleInOrganization}</Error>}
							</StyledField>
							<StyledField>
								<label>Phone</label>
								<Field id="phone" name="phone" data-testid="phone" />
								{errors.phone && touched.phone && <Error>{errors.phone}</Error>}
							</StyledField>
							<StyledField>
								<label>Email</label>
								<Field id="email" name="email" data-testid="email" />
								{errors.email && touched.email && <Error>{errors.email}</Error>}
							</StyledField>
							<StyledField>
								<label>Website</label>
								<Field id="website" name="website" data-testid="website" />
								{errors.website && touched.website && <Error>{errors.website}</Error>}
							</StyledField>
							<StyledField>
								<label>Office address</label>
								<Field id="officeAddress" name="officeAddress" data-testid="officeAddress" />
								{errors.officeAddress && touched.officeAddress && <Error>{errors.officeAddress}</Error>}
							</StyledField>
							<StyledField>
								<label>Private address</label>
								<Field id="privateAddress" name="privateAddress" data-testid="privateAddress" />
								{errors.privateAddress && touched.privateAddress && <Error>{errors.privateAddress}</Error>}
							</StyledField>
						</Col>
						<Col sm={12} lg={6} offset={{ xl: 3 }}>
							<StyledField>
								<label>About</label>
								<Field id="about" name="about" data-testid="about" as="textarea" />
								{errors.about && touched.about && <Error>{errors.about}</Error>}
							</StyledField>
						</Col>
					</Row>

					<Row>
						<StyledProfileDetailsDataButtons>
							<Button onClick={() => setIsEditMode(false)} icon={CancelIcon} testid="cancel">
								Cancel
							</Button>
							<Button type="submit" disabled={!isValid} icon={SaveIcon} testid="submit">
								Save changes
							</Button>
						</StyledProfileDetailsDataButtons>
					</Row>
				</Form>
			)}
		</Formik>
	);
}

ProfileDetailsDataEditForm.propTypes = {
	data: PropTypes.object,
	setIsEditMode: PropTypes.func,
};
